import { CartUpdateEvent } from '@theme/events';

/**
 * Native shipping protection (InsureShip product) for the cart drawer and the cart page.
 * Markup and pricing come from snippets/nv-shipping-protection.liquid. This module only
 * adds, removes or swaps the insurance line and then hands the fresh section HTML to the
 * theme, the same way the theme's own cart requests do.
 *
 * Tracking: dispatches `nv-shipping-protection:change` on document with
 * detail { action: 'add' | 'remove', source: 'shopper' | 'auto' }. Tier swaps and
 * orphan cleanups are not announced.
 */

const CHOICE_KEY = 'nv-sp-choice';
const SOURCE = 'nv-shipping-protection';

let busy = false;
const attemptsByState = new Map();

function readChoice() {
  try {
    return sessionStorage.getItem(CHOICE_KEY);
  } catch (_) {
    return null;
  }
}

function writeChoice(value) {
  try {
    sessionStorage.setItem(CHOICE_KEY, value);
  } catch (_) {
    /* private mode */
  }
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
}

/**
 * POST to a cart endpoint and ask Shopify to render the cart sections in the same response.
 * @param {string} url
 * @param {Record<string, unknown>} body
 */
async function cartPost(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      ...body,
      sections: cartSectionIds().join(','),
      sections_url: window.location.pathname,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || `Cart request failed (${response.status})`);
  }
  return data;
}

/** Visible item count, read from the freshly rendered section like the theme does. */
function itemCountFromSections(sections) {
  for (const html of Object.values(sections || {})) {
    if (!html) continue;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector('[ref="cartItemCount"]');
    if (node) return parseInt(node.textContent || '0', 10) || 0;
  }
  return 0;
}

function publish(data) {
  const sections = data.sections || {};
  document.dispatchEvent(
    new CartUpdateEvent(data, SOURCE, {
      source: SOURCE,
      itemCount: itemCountFromSections(sections),
      sections,
    })
  );
}

/**
 * Announce a change for tracking (Intelligems custom events listen to this).
 * @param {'add' | 'remove'} action
 * @param {'shopper' | 'auto'} source
 */
function announce(action, source) {
  try {
    document.dispatchEvent(new CustomEvent('nv-shipping-protection:change', { detail: { action, source } }));
  } catch (_) {
    /* tracking must never break the cart */
  }
}

function lineKeys(el) {
  return (el.dataset.lineKeys || '').split(',').filter(Boolean);
}

async function removeLines(keys) {
  const updates = {};
  for (const key of keys) updates[key] = 0;
  return cartPost('/cart/update.js', { updates });
}

async function addLine(variantId, amount) {
  return cartPost('/cart/add.js', {
    items: [{ id: variantId, quantity: 1, properties: { _insure_shipping_amount: amount } }],
  });
}

function openInfo() {
  const dialog = document.getElementById('nv-insure-dialog');
  if (dialog instanceof HTMLDialogElement && typeof dialog.showModal === 'function') {
    if (!dialog.open) dialog.showModal();
    return;
  }
  if (typeof window.openInsureShipPrivacyModal === 'function') window.openInsureShipPrivacyModal();
}

class NvShippingProtection extends HTMLElement {
  static observedAttributes = ['data-state', 'data-default'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.#schedule();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    clearTimeout(this.#timer);
  }

  attributeChangedCallback() {
    this.#schedule();
  }

  get #state() {
    const ds = this.dataset;
    return {
      desired: ds.desiredVariant ? Number(ds.desiredVariant) : null,
      amount: Number(ds.amount || 0),
      lineVariant: ds.lineVariant ? Number(ds.lineVariant) : null,
      lineCount: Number(ds.lineCount || 0),
      defaultOn: ds.default === 'true' || window.nvShippingProtectionDefault === true,
    };
  }

  #schedule(delay = 120) {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#sync(), delay);
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-nv-sp-info]')) {
      event.preventDefault();
      openInfo();
    }
  };

  #onChange = async (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('nv-sp__input')) return;

    const wantOn = input.checked;
    writeChoice(wantOn ? 'on' : 'off');

    if (busy) {
      input.checked = !wantOn;
      return;
    }

    const state = this.#state;
    const keys = lineKeys(this);
    this.setAttribute('aria-busy', 'true');
    busy = true;
    try {
      if (wantOn && state.desired && state.lineCount === 0) {
        publish(await addLine(state.desired, state.amount));
        announce('add', 'shopper');
      } else if (!wantOn && keys.length) {
        publish(await removeLines(keys));
        announce('remove', 'shopper');
      }
    } catch (error) {
      console.warn('[nv-shipping-protection]', error);
      input.checked = !wantOn;
    } finally {
      busy = false;
      this.removeAttribute('aria-busy');
    }
  };

  /** Keep the cart consistent with the rendered state: right tier, no orphan line, optional default. */
  async #sync() {
    if (!this.isConnected) return;
    if (busy) {
      this.#schedule(400);
      return;
    }

    const state = this.#state;
    const keys = lineKeys(this);
    let action = null;

    if (!state.desired) {
      if (state.lineCount > 0) action = 'remove';
    } else if (state.lineCount > 0) {
      if (state.lineCount !== 1 || state.lineVariant !== state.desired) action = 'replace';
    } else {
      const choice = readChoice();
      if (choice === 'on' || (choice === null && state.defaultOn)) action = 'add';
    }

    if (!action) return;

    // Never loop on a state the cart refuses to leave.
    const signature = `${this.dataset.state}|${action}`;
    const attempts = attemptsByState.get(signature) || 0;
    if (attempts >= 2) return;
    attemptsByState.set(signature, attempts + 1);

    busy = true;
    this.setAttribute('aria-busy', 'true');
    try {
      if (action === 'remove') {
        publish(await removeLines(keys));
      } else if (action === 'replace') {
        const removed = await removeLines(keys);
        try {
          publish(await addLine(state.desired, state.amount));
        } catch (error) {
          publish(removed);
          throw error;
        }
      } else if (action === 'add') {
        publish(await addLine(state.desired, state.amount));
        announce('add', 'auto');
      }
    } catch (error) {
      console.warn('[nv-shipping-protection]', error);
    } finally {
      busy = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('nv-shipping-protection')) {
  customElements.define('nv-shipping-protection', NvShippingProtection);
}
